import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import useManager from '../hooks/useManager';
import api, { fetchCsrfToken } from '../../shared/api';

interface FormData { rtp: number; }

const RTP_MIN_PERCENT = 50;
const RTP_MAX_PERCENT = 150;
const RTP_PRESETS = [50, 80, 94, 110, 125, 150];

export default function GlobalRtpControlFixed() {
  const { token } = useManager();
  const [current, setCurrent] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: { rtp: 94 },
  });
  const rtpVal = watch('rtp', 94);

  useEffect(() => {
    const t = token || localStorage.getItem('managerToken');
    if (!t) return;

    api.get('/manager/global-rtp', {
      headers: { Authorization: `Bearer ${t}` },
    }).then(({ data }) => {
      if (data.global_rtp_pct) {
        setCurrent(data.global_rtp_pct);
        setValue('rtp', data.global_rtp_pct);
      }
    }).catch(() => {});
  }, [token, setValue]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    const t = token || localStorage.getItem('managerToken');

    try {
      const csrf = await fetchCsrfToken();
      const res = await api.post('/manager/global-rtp', { rtp: data.rtp }, {
        headers: {
          Authorization: `Bearer ${t}`,
          'X-CSRF-Token': csrf,
        },
      });

      setCurrent(res.data.global_rtp_pct);
      toast.success(`Global RTP updated to ${res.data.global_rtp_pct}%`);
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || err.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const rtpColor = rtpVal > 100 ? 'text-red-600' : rtpVal >= 94 ? 'text-green-600' : 'text-yellow-600';

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Global RTP Control</h2>
      <p className="text-gray-500 mb-6 text-sm">
        This RTP applies to auto tables when a player uses Play Now without a table code.
      </p>

      {current !== null && (
        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center gap-4">
          <div className="text-4xl font-bold text-indigo-700">{current}%</div>
          <div>
            <div className="font-semibold text-indigo-800">Current Global RTP</div>
            <div className="text-sm text-indigo-500">Applied to auto-matched tables</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow p-6 space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            New Global RTP: <span className={`text-lg font-bold ${rtpColor}`}>{rtpVal}%</span>
          </label>
          <input
            type="range"
            min={RTP_MIN_PERCENT}
            max={RTP_MAX_PERCENT}
            step="1"
            className="w-full accent-indigo-600"
            {...register('rtp', {
              required: true,
              min: { value: RTP_MIN_PERCENT, message: `Min ${RTP_MIN_PERCENT}%` },
              max: { value: RTP_MAX_PERCENT, message: `Max ${RTP_MAX_PERCENT}%` },
              valueAsNumber: true,
            })}
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{RTP_MIN_PERCENT}%</span>
            <span>94%</span>
            <span>{RTP_MAX_PERCENT}%</span>
          </div>
          {errors.rtp && <p className="text-red-500 text-sm mt-1">{errors.rtp.message}</p>}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {RTP_PRESETS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setValue('rtp', v)}
              className={`py-2 rounded-lg border text-sm font-medium transition ${
                rtpVal === v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-indigo-50'
              }`}
            >
              {v}%
            </button>
          ))}
        </div>

        {rtpVal > 100 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            RTP above 100% should be used carefully.
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition disabled:opacity-50"
        >
          {loading ? 'Updating...' : 'Set Global RTP'}
        </button>
      </form>
    </div>
  );
}
